import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { clamp, roundTo } from '../types/geometry.js';
import { analyzeHighlights } from '../quality/highlight.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/** Face box narrower than this (analysis-buffer pixels) cannot be judged. */
const MIN_FACE_PIXEL_WIDTH = 24;
const MIN_SKIN_SAMPLES = 160;

/**
 * «Блики и пересветы».
 *
 * Clipped areas are measured separately for skin and for background. Only the
 * skin ratio drives the status: the white wall behind a document photo is
 * *supposed* to be near 255, so treating it as overexposure would reject exactly
 * the photos the documents ask for. The background number is still reported in
 * `details` for transparency.
 *
 * The check never fails: a soft skin highlight is a reason to re-shoot, not a
 * formal violation of the document's requirements.
 */
export const highlightCheck: ValidationCheck = {
  id: 'highlight',
  title: 'Блики и пересветы',
  description:
    'Измеряет выбитые светом участки отдельно на коже лица и отдельно на фоне; белый фон пересветом не считается.',
  kind: 'quality',
  requires: ['pixels', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'geometry']);
    if (missing) return notChecked(ctx, highlightCheck, missing, ctx.rule.severity);
    const pixels = ctx.pixels;
    const geometry = ctx.geometry;
    if (!pixels || !geometry) {
      return notChecked(
        ctx,
        highlightCheck,
        'Недостаточно данных для поиска пересветов на коже.',
        ctx.rule.severity,
      );
    }

    const maxSkinHighlightRatio = numberParam(ctx.rule.params, 'maxSkinHighlightRatio', 0.02);
    const warnSkinHighlightRatio = numberParam(ctx.rule.params, 'warnSkinHighlightRatio', 0.005);
    const nearWhiteLuma = numberParam(ctx.rule.params, 'nearWhiteLuma', 248);
    const uncertainBelowConfidence = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.35);

    const analysis = analyzeHighlights({
      buffer: pixels,
      geometry,
      options: { nearWhiteLuma },
    });

    const smallestRegion = analysis.regions.reduce(
      (min, region) => Math.min(min, region.samples),
      Number.POSITIVE_INFINITY,
    );
    const sizeScore = clamp(
      (Number.isFinite(smallestRegion) ? smallestRegion : 0) / 64,
      0,
      1,
    );
    /**
     * The confidence is capped by how large the face actually is in the analysis
     * buffer: a tiny face cannot be judged no matter how clean the ROI looks.
     */
    const faceScale = clamp(analysis.facePixelWidth / 80, 0, 1);
    const confidence = Math.min(
      clamp(0.3 + geometry.faceBoxConfidence.value * 0.5 + sizeScore * 0.2, 0, 1),
      Math.max(0.1, faceScale),
    );
    const tooSmall =
      analysis.facePixelWidth < MIN_FACE_PIXEL_WIDTH ||
      analysis.skinSamples < MIN_SKIN_SAMPLES ||
      confidence < uncertainBelowConfidence;

    /**
     * The documented bound is `maxSkinHighlightRatio`; the softer
     * `warnSkinHighlightRatio` is the point from which the result stops being a
     * clean pass. Both are warnings — a highlight is never a hard failure here.
     */
    const affected = analysis.regions.filter(
      (region) => region.clippedRatio > warnSkinHighlightRatio,
    );
    const severe = analysis.skinHighlightRatio > maxSkinHighlightRatio;

    const status: CheckResult['status'] = tooSmall
      ? 'uncertain'
      : affected.length > 0
        ? 'warning'
        : 'pass';

    const message =
      status === 'pass'
        ? '✅ Пересветов на коже нет'
        : status === 'warning'
          ? '⚠ На коже есть выбитые светом участки'
          : '⚠ Лицо на снимке слишком мелкое, чтобы надёжно оценить пересветы.';

    return plainResult({
      id: highlightCheck.id,
      title: highlightCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(analysis.skinHighlightRatio, 4),
      max: maxSkinHighlightRatio,
      unit: 'ratio',
      message,
      requirement: 'На коже лица не должно быть выбитых светом участков без деталей.',
      fixability: 'retake',
      geometry: analysis.regions.map((region) => ({
        type: 'rect' as const,
        coordinates: [
          roundTo(region.rect.x, 4),
          roundTo(region.rect.y, 4),
          roundTo(region.rect.width, 4),
          roundTo(region.rect.height, 4),
        ] as [number, number, number, number],
      })),
      problemRegions:
        status === 'pass'
          ? []
          : affected.map((region) => ({
              id: `highlight_skin_${region.id}`,
              rect: {
                x: roundTo(region.rect.x, 4),
                y: roundTo(region.rect.y, 4),
                width: roundTo(region.rect.width, 4),
                height: roundTo(region.rect.height, 4),
              },
              message: 'На этом участке кожи свет выбит — детали не видны.',
              severity: 'warning' as const,
            })),
      confidence: roundTo(confidence, 2),
      details: {
        skinHighlightRatio: roundTo(analysis.skinHighlightRatio, 4),
        backgroundWhiteRatio: roundTo(analysis.backgroundWhiteRatio, 4),
        clippedLuma: analysis.clippedLuma,
        roiMeans: analysis.roiMeans,
        skinSamples: analysis.skinSamples,
        backgroundSamples: analysis.backgroundSamples,
        facePixelWidth: roundTo(analysis.facePixelWidth, 1),
        affectedRegions: affected.map((region) => region.id),
        limits: { maxSkinHighlightRatio, warnSkinHighlightRatio, nearWhiteLuma },
        severe,
        backgroundWhiteIsNormal: true,
        displayValue: `${roundTo(analysis.skinHighlightRatio * 100, 1).toFixed(1).replace('.', ',')} %`,
      },
    });
  },
};
